import { useId, useRef } from 'react';
import { ButtonUI, ContextualMenu, showNotification } from 'sonata-react-components';

import { isSharedReportSession } from '@/helpers/shareReport/shareReport.helper';
import { useGlobalStore } from '@/store/global';
import { getSession } from '@/store/global/session/selectors';

import type { Placement } from '@floating-ui/react';
import type { ComponentProps } from 'react';

const DEFAULT_MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024;

type ImageEditMenuTexts = {
  /** Tooltip of the edit button. */
  edit: string;
  change: string;
  reset: string;
  fileTooLarge: string;
};

type Props = {
  /** File types offered by the file picker, as the `accept` attribute takes them. */
  accept: string;
  /** Places the edit button over the image it edits. */
  buttonClassName: string;
  buttonShape?: ComponentProps<typeof ButtonUI>['shape'];
  buttonSize?: ComponentProps<typeof ButtonUI>['size'];
  inputName: string;
  isLoading?: boolean;
  maxFileSizeBytes?: number;
  /** Where the menu opens, relative to the edit button. */
  tooltipPlacement?: Placement;
  texts: ImageEditMenuTexts;
  /** Receives the chosen file once it is within the size limit. */
  onUpload: (file: File) => void;
  /** Offers "reset" in the menu only when given. */
  onReset?: () => void;
};

/**
 * Edit button of a report image: its menu changes the image from a file (within a size limit)
 * or resets it. Hidden in shared report sessions, where the report is read-only.
 */
export default function ImageEditMenu({
  accept,
  buttonClassName,
  buttonShape = 'squareRounded',
  buttonSize = 'small',
  inputName,
  isLoading = false,
  maxFileSizeBytes = DEFAULT_MAX_FILE_SIZE_BYTES,
  onReset,
  onUpload,
  tooltipPlacement = 'bottom-end',
  texts
}: Readonly<Props>) {
  const menuId = useId();
  const inputId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isSharedReport = isSharedReportSession(useGlobalStore(getSession));

  if (isSharedReport) {
    return null;
  }

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawFile = e.target.files?.[0];
    e.target.value = '';
    if (!rawFile) {
      return;
    }
    if (rawFile.size > maxFileSizeBytes) {
      showNotification('error', texts.fileTooLarge);
      return;
    }
    onUpload(rawFile);
  };

  return (
    <>
      <ContextualMenu
        buttons={[
          {
            icon: 'fa-regular fa-arrow-up-to-line',
            id: `${menuId}-change`,
            onClick: () => fileInputRef.current?.click(),
            text: texts.change
          },
          ...(onReset
            ? [{ icon: 'fa-regular fa-rotate-right', id: `${menuId}-reset`, onClick: onReset, text: texts.reset }]
            : [])
        ]}
        id={menuId}
        placement={tooltipPlacement}
      >
        <ContextualMenu.Trigger>
          <ButtonUI
            className={buttonClassName}
            icon="fa-solid fa-pen-to-square"
            loading={isLoading}
            shape={buttonShape}
            size={buttonSize}
            title={texts.edit}
            type="secondary"
            visible
          />
        </ContextualMenu.Trigger>
      </ContextualMenu>
      <input
        accept={accept}
        data-testid="imageEditMenuInput"
        id={inputId}
        name={inputName}
        onChange={onInputChange}
        ref={fileInputRef}
        style={{ display: 'none' }}
        type="file"
      />
    </>
  );
}
